import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonService } from 'src/app/services/common.service';
import { ResponseMessageStatus, ResponseStatus } from 'src/app/shared/enums';
import { BadeLoftService } from '../bade-loft.service';

/**
 * "Sync from AfterShip" modal — shared by the Status and Shipped pages. Recovers an order whose
 * automatic AfterShip In Transit / Delivered update was missed.
 *
 * The server decides the ONE status this order may be synced to (In Transit until its stock is
 * consumed, then Delivered). This modal only offers that status and shows what each system did.
 */
@Component({
  selector: 'app-aftership-sync',
  templateUrl: './aftership-sync.component.html',
})
export class AfterShipSyncComponent implements OnInit {
  @Input() order: any; // a Status / Shipped row: id, invoiceNo, qB_TrackingNo
  @Output() hideModal = new EventEmitter<{ hide: boolean; synced: boolean }>();

  isVisible = true;
  isFetching = false;
  isSyncing = false;
  view: any = null;
  selectedStatus: string | null = null;
  result: any = null;
  private synced = false;

  constructor(private _blS: BadeLoftService, private _cS: CommonService) {}

  ngOnInit(): void {
    this.fetchStatus();
  }

  fetchStatus() {
    if (this.isFetching) return;
    this.isFetching = true;
    this.result = null;
    this._blS.getAfterShipStatus(this.order.id).subscribe(
      (res: any) => {
        this.isFetching = false;
        if (res?.responseStatus == ResponseStatus.Success) {
          this.view = res.result;
          this.selectedStatus = this.view?.allowedStatus || null;
        } else {
          this._cS.displayMessage(ResponseMessageStatus.error, res?.message || 'Could not read AfterShip.');
        }
      },
      () => {
        this.isFetching = false;
        this._cS.displayMessage(ResponseMessageStatus.error, 'Could not read AfterShip. Please try again.');
      }
    );
  }

  syncStatus() {
    if (!this.selectedStatus || this.isSyncing) return;
    this.isSyncing = true;
    this.result = null;
    const syncedDelivered = !this.isInTransit(this.selectedStatus);
    this._blS.afterShipManualSync({ assignmentId: this.order.id, status: this.selectedStatus }).subscribe(
      (res: any) => {
        this.isSyncing = false;
        this.result = res?.result || { isSuccess: false, message: res?.message, outcomes: [] };
        const isSuccess = res?.responseStatus == ResponseStatus.Success;
        this._cS.displayMessage(
          isSuccess ? ResponseMessageStatus.success : ResponseMessageStatus.error,
          res?.message || 'Sync finished.'
        );
        // Outcomes exist only when the sync actually ran — only then does the page list need a refresh.
        if (this.result?.outcomes?.length) this.synced = true;
        // Delivered is the last step: close and refresh the list. In Transit stays open so Delivered can follow;
        // a failure stays open so the operator can read it.
        if (isSuccess && syncedDelivered) {
          this.handleCancel();
          return;
        }
        // After In Transit consumes the stock the server moves this order on to Delivered (FRD sequence).
        const next = this.result?.nextAllowedStatus;
        if (next && this.view) {
          this.view.allowedStatus = next;
          this.view.inTransitCompleted = !this.isInTransit(next);
          this.selectedStatus = next;
        }
      },
      () => {
        this.isSyncing = false;
        this._cS.displayMessage(ResponseMessageStatus.error, 'Sync could not be completed. Please try again.');
      }
    );
  }

  isInTransit(status: string | null): boolean {
    return (status || '').replace(/\s/g, '').toLowerCase() === 'intransit';
  }

  label(status: string | null): string {
    if (!status) return '-';
    if (this.isInTransit(status)) return 'In Transit';
    return status.toLowerCase() === 'delivered' ? 'Delivered' : status;
  }

  tagColor(status: string | null): string {
    if (this.isInTransit(status)) return 'processing';
    return (status || '').toLowerCase() === 'delivered' ? 'success' : 'default';
  }

  helperText(): string {
    if (!this.selectedStatus) return '';
    return this.isInTransit(this.selectedStatus)
      ? "In Transit consumes this order's stock, marks it Completed, and updates QuickBooks and Shopify. Delivered becomes available after it."
      : 'Delivered updates QuickBooks and marks the Shopify fulfillment delivered (dated today). Stock is not touched.';
  }

  handleCancel() {
    // Closing mid-sync would emit before the outcome arrives and the page list would never refresh.
    if (this.isSyncing) return;
    this.isVisible = false;
    this.hideModal.emit({ hide: true, synced: this.synced });
  }
}
